import { CustomTaskOrder, CustomTaskOrderSlot, ID, Task } from '@griply/types';

import { insertDuplicatesAfterSelection } from './insert-duplicates-after-selection';

const VIEW_KEYS: Array<keyof CustomTaskOrder> = [
  'today',
  'allTasks',
  'inbox',
  'upcoming',
];

// Returns the minimal set of customTaskOrder slots that change when the given
// duplicate pairs are inserted: in every per-view, per-grouping order array
// where a selected original is present, the new ids are inserted as a block
// directly below the selection. Slots/arrays without a selected original are
// omitted so the persisted write stays minimal.
export const getDuplicatedCustomTaskOrder = (
  customTaskOrder: CustomTaskOrder | undefined,
  taskPairs: Array<{ oldTask: Task; newTask: Task }>,
): Partial<CustomTaskOrder> => {
  if (!customTaskOrder) {
    return {};
  }

  const result: Partial<CustomTaskOrder> = {};

  VIEW_KEYS.forEach((viewKey) => {
    const slot = customTaskOrder[viewKey];
    if (!slot) {
      return;
    }

    const updatedSlot = getUpdatedSlot(slot, taskPairs);
    if (updatedSlot) {
      result[viewKey] = updatedSlot;
    }
  });

  return result;
};

const getUpdatedSlot = (
  slot: CustomTaskOrderSlot,
  taskPairs: Array<{ oldTask: Task; newTask: Task }>,
): CustomTaskOrderSlot | null => {
  const updatedSlot: Record<string, ID[] | Record<ID, ID[]>> = {};
  let changed = false;

  Object.entries(slot).forEach(([groupingKey, value]) => {
    if (Array.isArray(value)) {
      const updated = insertDuplicatesAfterSelection(value, taskPairs);
      if (updated !== value) {
        updatedSlot[groupingKey] = updated;
        changed = true;
      }
      return;
    }

    const updatedGroup = getUpdatedGroup(value, taskPairs);
    if (updatedGroup) {
      updatedSlot[groupingKey] = updatedGroup;
      changed = true;
    }
  });

  return changed ? (updatedSlot as CustomTaskOrderSlot) : null;
};

const getUpdatedGroup = (
  group: Record<ID, ID[]>,
  taskPairs: Array<{ oldTask: Task; newTask: Task }>,
): Record<ID, ID[]> | null => {
  const updatedGroup: Record<ID, ID[]> = {};
  let changed = false;

  Object.entries(group).forEach(([itemId, ids]) => {
    const updated = insertDuplicatesAfterSelection(ids, taskPairs);
    if (updated !== ids) {
      updatedGroup[itemId] = updated;
      changed = true;
    }
  });

  return changed ? updatedGroup : null;
};
